import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG, EXTERNAL_APIS } from '@application/config';
import {
  EdgeDeviceResponse,
  IpifyResponse,
  ODataEquipmentResponse,
  CreateAccessPointRequest,
  UpdateAccessPointRequest,
  AccessPointResponse,
  ODataAccessPointResponse,
  ELogicalDirectionEnum,
  EvaluateByPersonIdRequest,
} from '../entities/access-edges.interface';

@Injectable({ providedIn: 'root' })
export class AccessPointEdgeService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiBase = this.appConfig.apiUrl.access_edge_manager_service;
  private readonly accessDirectoryApi = this.appConfig.apiUrl.access_directory_service;

  public getPublicIp(): Observable<IpifyResponse> {
    return this.http.get<IpifyResponse>('https://api.ipify.org?format=json');
  }

  public getPublicIpFallback(): Observable<{ ip: string }> {
    return this.http.get<{ ip: string }>(EXTERNAL_APIS.ipApi);
  }

  public loadInitialDevices(): Observable<EdgeDeviceResponse[]> {
    return this.http.get<EdgeDeviceResponse[]>(
      `${this.apiBase}/v1/edge-manager`,
      { withCredentials: true }
    );
  }

  public requestNetworkInfo(publicIp: string): Observable<string> {
    return this.http.post(
      `${this.apiBase}/v1/edge-command/request-network-info/${encodeURIComponent(publicIp)}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public bindEdge(edgeId: string): Observable<string> {
    return this.http.post(
      `${this.accessDirectoryApi}/BindEdge/${encodeURIComponent(edgeId)}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public unbindEdge(edgeId: string): Observable<string> {
    return this.http.post(
      `${this.accessDirectoryApi}/UnbindEdge/${encodeURIComponent(edgeId)}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public getEquipmentByEdgeId(edgeId: string): Observable<ODataEquipmentResponse> {
    const params = new HttpParams().set('$filter', `EdgeId eq '${edgeId}'`);
    return this.http.get<ODataEquipmentResponse>(
      `${this.accessDirectoryApi}/OData/Equipments`,
      { params, withCredentials: true }
    );
  }

  public getByEdgeId(edgeId: string): Observable<any> {
    return this.http.get<any>(
      `${this.accessDirectoryApi}/Equipment/GetByEdgeId`,
      { 
        params: {
          edgeId
        },
        withCredentials: true }
    );
  }

  public getAccessPoints(): Observable<ODataAccessPointResponse> {
    const params = new HttpParams().set('$expand', 'EdgeConfigurationDeployment,CameraDevice');
    return this.http.get<ODataAccessPointResponse>(
      `${this.accessDirectoryApi}/odata/AccessPoints`,
      { params, withCredentials: true }
    );
  }

  public createAccessPoint(data: CreateAccessPointRequest): Observable<AccessPointResponse> {
    return this.http.post<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint`,
      data,
      { withCredentials: true }
    );
  }

  public updateAccessPoint(data: UpdateAccessPointRequest): Observable<AccessPointResponse> {
    return this.http.patch<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint`,
      data,
      { withCredentials: true }
    );
  }

  public getAccessPointById(accessPointId: string): Observable<AccessPointResponse> {
    return this.http.get<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint/${encodeURIComponent(accessPointId)}`,
      { withCredentials: true }
    );
  }

  public deleteAccessPoint(accessPointId: string): Observable<string> {
    const params = new HttpParams().set('id', accessPointId);
    return this.http.delete(
      `${this.accessDirectoryApi}/AccessPoint`,
      { params, responseType: 'text', withCredentials: true }
    );
  }

  public releaseEdge(edgeId: string, direction: ELogicalDirectionEnum): Observable<string> {
    return this.http.post(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/release/${direction}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public evaluateByPersonId(edgeId: string, request: EvaluateByPersonIdRequest): Observable<any> {
    return this.http.post(
      `${this.apiBase}/AccessEvaluation/EvaluateByPersonId?edgeId=${encodeURIComponent(edgeId)}`,
      request,
      { withCredentials: true }
    );
  }

  public getWebcams(): Observable<Array<{ Index: number; InstanceId: string; Name: string }>> {
    return this.http.get<Array<{ Index: number; InstanceId: string; Name: string }>>(
      `${this.appConfig.apiUrl.acessoLocalhost}/GetWebcams`
    );
  }

  public captureSnapshot(edgeId: string): Observable<string | number[]> {
    return this.http.post<string | number[]>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/snapshot`,
      null,
      { responseType: 'json', withCredentials: true }
    );
  }
}

